import { createBrowserRouter, Navigate, redirect } from 'react-router-dom'

import { AppShell } from '@/components/layout/app-shell'
import { PublicLayout } from '@/components/layout/public-layout'
import { RouteErrorFallback } from '@/components/shared/route-error-fallback'
import { api } from '@/lib/api-client'
import { AuthLayout } from '@/routes/_auth/auth-layout'

export async function privateLoader() {
  try {
    const { data } = await api.get<{ user?: unknown } | null>('/api/auth/get-session')
    if (!data?.user) {
      const next = encodeURIComponent(window.location.pathname + window.location.search)
      // React Router loaders sinalizam redirect via `throw new Response(...)` — pattern oficial.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect(`/sign-in?next=${next}`)
    }
    return data.user
  } catch (err) {
    if (err instanceof Response) throw err
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect('/sign-in')
  }
}

export async function requirePermission(permission: string) {
  try {
    const { data } = await api.get<{ user?: { id: string }; permissions?: string[] } | null>(
      '/api/auth/get-session',
    )
    if (!data?.user) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect('/sign-in')
    }
    if (!data.permissions?.includes(permission)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect('/app')
    }
    return null
  } catch (err) {
    if (err instanceof Response) throw err
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect('/sign-in')
  }
}

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/ecosystem/pages/landing-page').then((m) => ({
            Component: m.LandingPage,
          })),
      },
      {
        path: '/sobre',
        lazy: () =>
          import('@/features/ecosystem/pages/about-page').then((m) => ({
            Component: m.AboutPage,
          })),
      },
      {
        path: '/ecosystem/:slug',
        lazy: () =>
          import('@/features/ecosystem/pages/unit-page').then((m) => ({
            Component: m.UnitPage,
          })),
      },
      {
        path: '/ecosystem/:unitSlug/:productSlug',
        lazy: () =>
          import('@/features/ecosystem/pages/product-page').then((m) => ({
            Component: m.ProductPage,
          })),
      },
    ],
  },

  {
    element: <AuthLayout />,
    children: [
      {
        path: '/sign-in',
        lazy: () =>
          import('@/features/auth/pages/sign-in-page').then((m) => ({ Component: m.SignInPage })),
      },
      {
        path: '/forgot-password',
        lazy: () =>
          import('@/features/auth/pages/forgot-password-page').then((m) => ({
            Component: m.ForgotPasswordPage,
          })),
      },
      {
        path: '/reset-password',
        lazy: () =>
          import('@/features/auth/pages/reset-password-page').then((m) => ({
            Component: m.ResetPasswordPage,
          })),
      },
    ],
  },

  {
    path: '/app',
    element: <AppShell />,
    loader: privateLoader,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/admin/pages/home-page').then((m) => ({ Component: m.HomePage })),
      },
      {
        path: 'admin/users',
        lazy: () =>
          import('@/features/admin/pages/users-page').then((m) => ({ Component: m.UsersPage })),
        loader: () => requirePermission('admin:users'),
      },
      {
        path: 'admin/ecosystem/units',
        lazy: () =>
          import('@/features/admin/pages/ecosystem-units-page').then((m) => ({
            Component: m.EcosystemUnitsPage,
          })),
        loader: () => requirePermission('admin:ecosystem'),
      },
      {
        path: 'admin/ecosystem/products',
        lazy: () =>
          import('@/features/admin/pages/ecosystem-products-page').then((m) => ({
            Component: m.EcosystemProductsPage,
          })),
        loader: () => requirePermission('admin:ecosystem'),
      },
      {
        path: 'admin/ecosystem/tracking',
        lazy: () =>
          import('@/features/admin/pages/ecosystem-tracking-page').then((m) => ({
            Component: m.EcosystemTrackingPage,
          })),
        loader: () => requirePermission('admin:ecosystem'),
      },
    ],
  },

  { path: '*', element: <Navigate to="/" replace /> },
])
